import { z } from 'zod';

import type { SystemDiskRecord } from '../../../capabilities/image-vm/types';
import type { ServiceFormItem } from '../../../capabilities/service/types';
import type { SharedVolumeRecord } from '../../../capabilities/shared-volume/types';
import type { DataDiskRecord } from '../../../capabilities/storage/types';
import { PricingMetaSchema } from '../../../components/vendorDevice';

const GpuSchema = z
  .object({
    vendor: z.string().optional(),
    device: z.string().optional(),
    vendorID: z.string().optional(),
    deviceID: z.string().optional(),
    number: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    code: z.string().optional(),
    deviceType: z.string().optional(),
    quantityUnit: z.string().optional(),
  })
  .optional();

export const VmSpecSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  runStrategy: z.string().optional(),
  workspace: z.string().optional(),
  datacenter: z.string().optional(),
  /** 选项类字段原始元数据（展示优先名称，不参与 apiBody） */
  optionMeta: z.record(z.string(), z.unknown()).optional(),
  system: z
    .object({
      architecture: z.string().optional(),
      os: z.string().optional(),
      version: z.string().optional(),
      bootloader: z.string().optional(),
      options: z
        .object({
          containerDeployConfig: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
        })
        .optional(),
    })
    .optional(),
  image: z.string().optional(),
  source: z.unknown().optional(),
  resource: z.object({
    memory: z.union([z.number(), z.string()]).optional(),
    cpu: z.object({
      physicalCards: z.number().optional(),
      physicalCores: z.number().optional(),
      logicalCores: z.number().optional(),
      pricing: PricingMetaSchema,
    }),
    gpu: GpuSchema,
    gpuPricing: PricingMetaSchema,
    network: z.object({
      vpc: z.object({ name: z.string() }).optional(),
      subnet: z.object({ name: z.string().optional() }),
      securitygroup: z.object({ name: z.string().optional() }).optional(),
      Bandwidth: z.union([z.number(), z.string()]).optional(),
      bandwidthPricing: PricingMetaSchema,
      dnsConfig: z
        .object({
          nameservers: z.array(z.string()).optional(),
          searches: z.array(z.string()).optional(),
          options: z.array(z.object({ name: z.string(), value: z.string() })).optional(),
        })
        .optional(),
      hpc: z.object({ elasticRdma: z.array(z.unknown()).optional() }).optional(),
      service: z.array(z.custom<ServiceFormItem>()).optional(),
    }),
    memoryPricing: PricingMetaSchema,
    cpuPricing: PricingMetaSchema,
  }),
  systemDisk: z.custom<SystemDiskRecord>().optional(),
  dataDisks: z.array(z.custom<DataDiskRecord>()).optional(),
  volumes: z.array(z.custom<SharedVolumeRecord>()).optional(),
});

export type VmSpec = z.infer<typeof VmSpecSchema>;

export type { VmDeliveryMetadata } from './deliveryMetadata';
export type VmApiPayload = Record<string, unknown>;
